import { MetaEvent } from '@/types';
import { usePage } from '@inertiajs/react';
import { useEffect } from 'react';

/**
 * GA4 event names for the conversions the server reports to Meta under Meta's
 * names. The GTM lookup table (FBEventName) maps them back to Meta's names for
 * the Meta Pixel tag, so one push feeds GA4 and Meta.
 */
const GA4_EVENT_NAMES: Record<string, string> = {
  Lead: 'generate_lead',
  CompleteRegistration: 'sign_up',
  Purchase: 'purchase',
  Subscribe: 'subscribe',
};

/**
 * Pushes the client-side half of a server-tracked conversion to GTM.
 *
 * `metaEvent` is shared for every response by HandleInertiaRequests, but only
 * carries data on the one request right after a controller calls
 * MetaConversionsApiService::send() and flashes it. It is pushed with the same
 * event_id the server used, so the Meta Pixel tag in the GTM container can pass
 * it as the event ID and Meta can deduplicate the browser and server copies.
 *
 * Mounted once in AppLayout, next to <Seo />. Absent on admin routes, where
 * GTM itself isn't loaded (see app.blade.php).
 */
const MetaPixelEvents = () => {
  const { metaEvent } = usePage<{ metaEvent: MetaEvent | null }>().props;

  useEffect(() => {
    if (!metaEvent) return;

    const event = GA4_EVENT_NAMES[metaEvent.name] ?? metaEvent.name;
    const { currency, value, content_ids, num_items } =
      metaEvent.customData as {
        currency?: string;
        value?: number;
        content_ids?: string[];
        num_items?: number;
      };

    window.dataLayer?.push({
      event,
      event_id: metaEvent.id,
      ...(!content_ids && value !== undefined && { currency, value }),
      ...(content_ids && {
        ecommerce: {
          transaction_id: metaEvent.id,
          currency,
          value,
          items: content_ids.map((item_id) => ({
            item_id,
            quantity: num_items ?? 1,
          })),
        },
      }),
    });
    // Only the event identity should re-trigger this — not a new object
    // reference on every Inertia response that happens to carry the same event.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metaEvent?.id]);

  return null;
};

export default MetaPixelEvents;
